import 'bootstrap/dist/css/bootstrap.min.css';
import React, { Component } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import * as Constants from './constants';
import { DownloadMapContainer } from './DownloadMapContainer';
import { ManufacturerSelectBox } from './ManufacturerSelectBox';
import { MarketComparison } from './MarketComparison';
import { MonthlySectionDataReport } from './MonthlySectionDataReport';
import { ProductDecisions } from './ProductDecisions';
import { WinLossMapContainer } from './WinLossMapContainer';

const TAB_ID_ENHANCED_LISTING = "enhancedListing";
const TAB_ID_SECTION_OPENS = "sectionOpens";
const TAB_ID_PRODUCT_DECISIONS = "productDecisions";
const TAB_ID_PRODUCT_DECISIONS_OVERALL = "productDecisionsOverall";
const TAB_ID_PRODUCT_DECISIONS_WON = "productDecisionsWon";
const TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE = "productDecisionsWinPercent";
const TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP = "productDecisionsWinLossMap";
const TAB_ID_MARKET_COMPARISON = "marketComparison";
const TAB_ID_PRODUCT_MASTERSPEC = "productMasterSpec";
const TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS = "productMasterSpecDownloads";
const TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP = "productMasterSpecDownloadMap";

export class BpmCharts extends Component {
    constructor(props) {
        super(props);
        const currentYear = new Date().getFullYear();
        this.handleManufacturerChanged = this.handleManufacturerChanged.bind(this);
        this.handleSectionChanged = this.handleSectionChanged.bind(this);
        this.handleProductTypeChanged = this.handleProductTypeChanged.bind(this);
        this.handleStartYearChanged = this.handleStartYearChanged.bind(this);
        this.state = {
            manufacturers: null,
            rawSectionOpensData: null,
            rawProductDecisionsData: null,
            rawMarketComparisonData: null,
            rawSectionDownloadsData: null,
            rawWinLossMapData: null,
            rawDownloadMapData: null,
            sections: null,
            selectedSectionNumber: null,
            selectedStartYear: currentYear - 2, // To limit size of graphs, by default show current year's data and previous two years
            selectedProductTypeName: Constants.ALL_PRODUCT_TYPES,
            isLoading: false,
            fetchError: null,
            selectedManufacturerId: this.props.manufacturerId,
            selectedTopLevelTab: this.props.selectedTopLevelTab || TAB_ID_ENHANCED_LISTING,
            selectedEnhancedListingTab: this.props.selectedEnhancedListingTab || TAB_ID_SECTION_OPENS,
            selectedProductDecisionsTab: this.props.selectedProductDecisionsTab || TAB_ID_PRODUCT_DECISIONS_OVERALL,
            selectedProductMasterSpecTab: this.props.selectedProductMasterSpecTab || TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS
        }
    }

    componentDidMount() {
        const { userToken } = this.props;
        if (userToken) {
            this.setState({ isLoading: true });
            const endpoint = `${Constants.API_ENDPOINT}manufacturers/jwt`;
            fetch(endpoint, {
                mode: 'cors',
                headers: {
                    Authorization: 'Bearer ' + userToken,
                    Accept: '*/*',
                    Pragma: 'no-cache',
                    'Cache-Control': 'no-cache',
                },
            })
                .then((response) => {
                    if (response.ok) {
                        return response.json()
                    } else {
                        throw new Error('Error fetching manufacturers.')
                    }
                })
                .then((data) => {
                    // If user only has access to a single manufacturer (the usual case for non-admins)
                    // then default to that manufacturer (the manufacturer selection list won't be shown)
                    if (data.length === 1) {
                        this.handleManufacturerChanged(data[0].id)
                    } else {
                        this.setState(
                            { manufacturers: data, isLoading: false },
                            () => {
                                // If a default manufacturer was set, load its sections.
                                if (this.state.selectedManufacturerId) {
                                    this.handleManufacturerChanged(
                                        this.state.selectedManufacturerId
                                    )
                                }
                            }
                        )
                    }
                })
                .catch((error) => this.setState({ fetchError: error }))
        }
    }

    handleManufacturerChanged(manufacturerId) {
        this.setState(
            {
                selectedManufacturerId: manufacturerId,
                sections: null,
                rawSectionOpensData: null,
                rawProductDecisionsData: null,
                rawMarketComparisonData: null,
                rawSectionDownloadsData: null,
                rawWinLossMapData: null,
                rawDownloadMapData: null,
                isLoading: !!manufacturerId && manufacturerId !== Constants.PLACEHOLDER_MANUFACTURER
            }, () => this._loadSelectedManufacturerData());
    }

    _loadSelectedManufacturerData() {
        const { selectedManufacturerId } = this.state;
        if (!selectedManufacturerId || selectedManufacturerId === Constants.PLACEHOLDER_MANUFACTURER) {
            return;
        }

        this.setState({ isLoading: true }, () => {
            if (this._getActiveChartTab() === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS) {
                this._loadActiveChartData();
            } else {
                this._loadActiveChartSections();
            }
        });
    }

    _loadActiveChartSections() {
        if (this.state.sections) {
            this._loadActiveChartData();
        } else {
            const { userToken } = this.props;
            if (userToken) {
                const endpoint = `${Constants.API_ENDPOINT}sections/jwt?manufacturerId=${this.state.selectedManufacturerId}`;
                fetch(endpoint, {
                    mode: 'cors',
                    headers: {
                        Authorization: 'Bearer ' + userToken,
                        Accept: '*/*',
                        Pragma: 'no-cache',
                        'Cache-Control': 'no-cache',
                    },
                })
                    .then((response) => response.json())
                    .then((data) =>
                        this.setState(
                            {
                                sections: data.filter(
                                    (d) => d.name && d.name.length > 0
                                ),
                                selectedSectionNumber: this._getDefaultSectionNumber(
                                    data
                                ),
                            },
                            () => this._loadActiveChartData()
                        )
                    )
                    .catch((error) => this.setState({ fetchError: error }))
            }
        }
    }

    _getDefaultSectionNumber(sections) {
        if (!sections || sections.length === 0) {
            return null;
        }

        const { defaultSectionNumber } = this.props;
        if (defaultSectionNumber
            && sections.some(sec => sec.number === defaultSectionNumber)) {
            return defaultSectionNumber;
        }

        return sections[0].number;
    }

    _loadActiveChartData() {
        const activeTab = this._getActiveChartTab();
        if (activeTab === TAB_ID_SECTION_OPENS) {
            this._loadSectionOpensChartData();
        } else if (activeTab === TAB_ID_PRODUCT_DECISIONS_OVERALL
            || activeTab === TAB_ID_PRODUCT_DECISIONS_WON
            || activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE) {
            this._loadProductDecisionsChartData();
        } else if (activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS) {
            this._loadSectionDownloadsChartData();
        } else if (activeTab === TAB_ID_MARKET_COMPARISON) {
            this._loadMarketComparisonChartData();
        } else if (activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP) {
            this._loadWinLossMapData();
        } else if (activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP) {
            this._loadDownloadMapData();
        } else {
            this.setState({ isLoading: false });
        }
    }

    handleSectionChanged(sectionNumber) {
        const activeTab = this._getActiveChartTab();
        if (activeTab === TAB_ID_MARKET_COMPARISON) {
            this.setState({
                isLoading: true,
                selectedSectionNumber: sectionNumber,
                rawMarketComparisonData: null
            }, () => this._loadMarketComparisonChartData());
        } else if (activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP) {
            this.setState({
                isLoading: true,
                selectedSectionNumber: sectionNumber,
                rawWinLossMapData: null
            }, () => this._loadWinLossMapData());
        } else if (activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP) {
            this.setState({
                isLoading: true,
                selectedSectionNumber: sectionNumber,
                rawDownloadMapData: null
            }, () => this._loadDownloadMapData());
        } else {
            this.setState({ selectedSectionNumber: sectionNumber, selectedProductTypeName: null });
        }
    }

    handleStartYearChanged(startYear) {
        this.setState({ selectedStartYear: startYear });
    }

    handleProductTypeChanged(productTypeName) {
        this.setState({ selectedProductTypeName: productTypeName });
    }

    _loadSectionOpensChartData() {
        this._fetchSelectedManufacturerData("sectionOpens", "rawSectionOpensData");
    }

    _loadSectionDownloadsChartData() {
        this._fetchSelectedManufacturerData("productMasterSpecSectionDownloads", "rawSectionDownloadsData");
    }

    _loadProductDecisionsChartData() {
        this._fetchSelectedManufacturerData("productDecisions", "rawProductDecisionsData");
    }

    _loadMarketComparisonChartData() {
        this._fetchSelectedManufacturerData("marketRankings", "rawMarketComparisonData", true);
    }

    _loadWinLossMapData() {
        this._fetchSelectedManufacturerData("locatedProductDecisions", "rawWinLossMapData", true);
    }

    _loadDownloadMapData() {
        this._fetchSelectedManufacturerData("locatedSectionDownloads", "rawDownloadMapData", true);
    }


    _fetchSelectedManufacturerData(endpointSuffix, stateDataProperty, includeSection = false) {
        if (this.state[stateDataProperty]) {
            this.setState({ isLoading: false });
        } else {
            const { userToken } = this.props;
            if (userToken) {
                let endpoint = `${Constants.API_ENDPOINT}${endpointSuffix}/jwt?manufacturerId=${this.state.selectedManufacturerId}`;
                if (includeSection) {
                    endpoint = `${endpoint}&sectionNumber=${this.state.selectedSectionNumber}`;
                }

                fetch(endpoint, {
                    mode: 'cors',
                    headers: {
                        Authorization: 'Bearer ' + userToken,
                        Accept: '*/*',
                        Pragma: 'no-cache',
                        'Cache-Control': 'no-cache',
                    },
                })
                    .then((response) => response.json())
                    .then((data) =>
                        this.setState({
                            [stateDataProperty]: data,
                            isLoading: false,
                        })
                    )
                    .catch((error) => this.setState({ fetchError: error }))
            }
        }
    }

    _getActiveChartTab() {
        // I know. The problem is, clicking a nested tab having its own nested tabs doesn't fire onSelect() for the leaf-level tab.
        // If there's a better way to determine which leaf-level tab is active, feel free to replace this.
        if (this.state.selectedTopLevelTab === TAB_ID_ENHANCED_LISTING) {
            if (this.state.selectedEnhancedListingTab === TAB_ID_SECTION_OPENS) {
                return TAB_ID_SECTION_OPENS;
            } else if (this.state.selectedEnhancedListingTab === TAB_ID_PRODUCT_DECISIONS) {
                if (this.state.selectedProductDecisionsTab === TAB_ID_PRODUCT_DECISIONS_OVERALL) {
                    return TAB_ID_PRODUCT_DECISIONS_OVERALL;
                } else if (this.state.selectedProductDecisionsTab === TAB_ID_PRODUCT_DECISIONS_WON) {
                    return TAB_ID_PRODUCT_DECISIONS_WON;
                } else if (this.state.selectedProductDecisionsTab === TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE) {
                    return TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE;
                } else if (this.state.selectedProductDecisionsTab === TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP) {
                    return TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP;
                }
            } else if (this.state.selectedEnhancedListingTab === TAB_ID_MARKET_COMPARISON) {
                return TAB_ID_MARKET_COMPARISON;
            }
        } else if (this.state.selectedTopLevelTab === TAB_ID_PRODUCT_MASTERSPEC) {
            if (this.state.selectedProductMasterSpecTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS) {
                return TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS;
            } else if (this.state.selectedProductMasterSpecTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP) {
                return TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP;
            }
        }

        return null;
    }

    _setActiveTopLevelTab(tabId) {
        this.setState({ selectedTopLevelTab: tabId }, () => this._loadSelectedManufacturerData());
    }

    _setActiveEnhancedListingsTab(tabId) {
        this.setState({ selectedEnhancedListingTab: tabId }, () => this._loadSelectedManufacturerData());
    }

    _setActiveProductMasterSpecTab(tabId) {
        this.setState({ selectedProductMasterSpecTab: tabId }, () => this._loadSelectedManufacturerData());
    }

    _setActiveProductDecisionsTab(tabId) {
        this.setState({ selectedProductDecisionsTab: tabId }, () => this._loadSelectedManufacturerData());
    }

    _getSectionListFromRawData(rawData) {
        if (!rawData) {
            return null;
        }

        const sectionKeys = new Set(rawData.map(d => `${d.sectionNumber}::${d.sectionTitle}`));
        const sections = Array.from(sectionKeys)
            .sort()
            .map(key => {
                const elements = key.split("::");
                return {
                    number: elements[0],
                    name: elements[1]
                };
            });
        return sections;
    }

    render() {
        const { fetchError } = this.state;
        if (fetchError) {
            throw fetchError; // throw error here rather than in fetch promises themselves, so that they'll be caught by ErrorBoundary.
        }

        const {
            isLoading,
            selectedProductTypeName,
            selectedSectionNumber,
            manufacturers,
            selectedStartYear
        } = this.state;

        if (manufacturers && manufacturers.length === 0) {
            return <p>Your login is not associated with any manufacturers. If your subscription is still active. If this message persists, please  <a href="https://deltek.custhelp.com">contact support</a>.</p>;
        }

        const mfgList = manufacturers
            ? <ManufacturerSelectBox
                manufacturers={manufacturers}
                onManufacturerChanged={this.handleManufacturerChanged}
                selectedManufacturer={this.state.selectedManufacturerId} />
            : null;
        const activeTab = this._getActiveChartTab();
        const sections = activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS
            ? this._getSectionListFromRawData(this.state.rawSectionDownloadsData)
            : this.state.sections;

        return (
            <div>
                {mfgList}
                <Tabs justify activeKey={this.state.selectedToplevelTab} id="topLevelTabs"
                    onSelect={k => this._setActiveTopLevelTab(k)}
                    className={activeTab === TAB_ID_PRODUCT_MASTERSPEC
                        ? "active-tab-container"
                        : ""}
                >
                    <Tab eventKey={TAB_ID_ENHANCED_LISTING} title="Enhanced Listing">
                        <Tabs justify activeKey={this.state.selectedEnhancedListingTab} id="enhancedListingTabs"
                            onSelect={k => this._setActiveEnhancedListingsTab(k)}
                            className={activeTab === TAB_ID_SECTION_OPENS || activeTab === TAB_ID_MARKET_COMPARISON
                                ? "active-tab-container"
                                : ""}>
                            <Tab className="leaf-level-tab" eventKey={TAB_ID_SECTION_OPENS} title="Family Opens">
                                {activeTab === TAB_ID_SECTION_OPENS
                                    && (<MonthlySectionDataReport
                                        yPropertyName="opens"
                                        yAxisLabel="Family Opens"
                                        isLoading={isLoading}
                                        sections={sections}
                                        sectionNumber={selectedSectionNumber}
                                        startYear={selectedStartYear}
                                        rawData={this.state.rawSectionOpensData}
                                        handleSectionChanged={this.handleSectionChanged}
                                        handleStartYearChanged={this.handleStartYearChanged} />)}
                            </Tab>
                            <Tab eventKey={TAB_ID_PRODUCT_DECISIONS} title="Product Decisions">
                                <Tabs justify activeKey={this.state.selectedProductDecisionsTab} id="productDecisionsTabs"
                                    onSelect={k => this._setActiveProductDecisionsTab(k)}
                                    className={activeTab === TAB_ID_PRODUCT_DECISIONS_OVERALL || activeTab === TAB_ID_PRODUCT_DECISIONS_WON || activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE
                                        ? "active-tab-container"
                                        : ""}>
                                    <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_DECISIONS_OVERALL} title="Overall">
                                        {activeTab === TAB_ID_PRODUCT_DECISIONS_OVERALL
                                            && (<ProductDecisions
                                                yPropertyName="total"
                                                yAxisLabel="Overall Decisions"
                                                isLoading={isLoading}
                                                sections={sections}
                                                sectionNumber={selectedSectionNumber}
                                                productTypeName={selectedProductTypeName}
                                                rawData={this.state.rawProductDecisionsData}
                                                handleSectionChanged={this.handleSectionChanged}
                                                handleProductTypeChanged={this.handleProductTypeChanged}
                                                startYear={selectedStartYear}
                                                handleStartYearChanged={this.handleStartYearChanged} />)}
                                    </Tab>
                                    <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_DECISIONS_WON} title="Won">
                                        {activeTab === TAB_ID_PRODUCT_DECISIONS_WON
                                            && (<ProductDecisions
                                                yPropertyName="won"
                                                yAxisLabel="Decisions Won"
                                                isLoading={isLoading}
                                                sections={sections}
                                                sectionNumber={selectedSectionNumber}
                                                productTypeName={selectedProductTypeName}
                                                rawData={this.state.rawProductDecisionsData}
                                                handleSectionChanged={this.handleSectionChanged}
                                                handleProductTypeChanged={this.handleProductTypeChanged}
                                                startYear={selectedStartYear}
                                                handleStartYearChanged={this.handleStartYearChanged} />)}
                                    </Tab>
                                    <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE} title="Win Percentage">
                                        {activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_PERCENTAGE
                                            && (<ProductDecisions
                                                yPropertyName="winPercentage"
                                                yAxisLabel="Win %"
                                                isLoading={isLoading}
                                                sections={sections}
                                                sectionNumber={selectedSectionNumber}
                                                productTypeName={selectedProductTypeName}
                                                rawData={this.state.rawProductDecisionsData}
                                                handleSectionChanged={this.handleSectionChanged}
                                                handleProductTypeChanged={this.handleProductTypeChanged}
                                                startYear={selectedStartYear}
                                                handleStartYearChanged={this.handleStartYearChanged} />)}
                                    </Tab>
                                    <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP} title="Win/Loss Map">
                                        {activeTab === TAB_ID_PRODUCT_DECISIONS_WIN_LOSS_MAP
                                            && (<WinLossMapContainer
                                                isLoading={isLoading}
                                                rawData={this.state.rawWinLossMapData}
                                                sections={sections}
                                                sectionNumber={selectedSectionNumber}
                                                handleSectionChanged={this.handleSectionChanged}
                                                productTypeName={selectedProductTypeName}
                                                handleProductTypeChanged={this.handleProductTypeChanged}
                                            />)}
                                    </Tab>
                                </Tabs>
                            </Tab>
                            <Tab className="leaf-level-tab" eventKey={TAB_ID_MARKET_COMPARISON} title="Market Comparison">
                                {activeTab === TAB_ID_MARKET_COMPARISON ?
                                    (<MarketComparison
                                        manufacturerId={this.state.selectedManufacturerId}
                                        isLoading={isLoading}
                                        sections={sections}
                                        sectionNumber={selectedSectionNumber}
                                        rawData={this.state.rawMarketComparisonData}
                                        handleSectionChanged={this.handleSectionChanged}
                                        startYear={selectedStartYear}
                                        handleStartYearChanged={this.handleStartYearChanged} />)
                                    : null}
                            </Tab>
                        </Tabs>
                    </Tab>
                    <Tab eventKey={TAB_ID_PRODUCT_MASTERSPEC} title="Product MasterSpec">
                        <Tabs justify activeKey={this.state.selectedProductMasterSpecTab} id="productMasterSpecTabs"
                            onSelect={k => this._setActiveProductMasterSpecTab(k)}
                            className={activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS || activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP
                                ? "active-tab-container"
                                : ""}>
                            <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS} title="Family Downloads">
                                {activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOADS
                                    && (<MonthlySectionDataReport
                                        yPropertyName="downloads"
                                        yAxisLabel="Family Downloads"
                                        isLoading={isLoading}
                                        sections={sections}
                                        sectionNumber={selectedSectionNumber}
                                        startYear={selectedStartYear}
                                        rawData={this.state.rawSectionDownloadsData}
                                        handleSectionChanged={this.handleSectionChanged}
                                        handleStartYearChanged={this.handleStartYearChanged} />)}
                            </Tab>
                            <Tab className="leaf-level-tab" eventKey={TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP} title="Family Download Map">
                                {activeTab === TAB_ID_PRODUCT_MASTERSPEC_DOWNLOAD_MAP
                                    && (<DownloadMapContainer
                                        isLoading={isLoading}
                                        rawData={this.state.rawDownloadMapData}
                                        sections={sections}
                                        sectionNumber={selectedSectionNumber}
                                        handleSectionChanged={this.handleSectionChanged}
                                    />)}
                            </Tab>
                        </Tabs>
                    </Tab>
                </Tabs>
            </div>
        );
    }
}
